import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { getProductsByIDs } from '@adornis/buchungsheld-db/api/product.js';
import { getUserByID } from '@adornis/buchungsheld-db/api/users.js';
import { getWorkshopByID } from '@adornis/buchungsheld-db/api/workshop.js';
import { type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { PaymentPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import { BHPayment } from '@adornis/buchungsheld-db/entities/BHPayment.js';
import type { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { ChosenProduct } from '@adornis/payments/db/chosenProduct.js';
import { Price } from '@adornis/payments/db/prices.js';
import { isBefore, subDays } from 'date-fns';

export const createPaymentFromParticipations = async (
  institutionID: string,
  participations: NarrowedEntity<Participation, { _id: 1; userID; productID: 1 }>[],
  getProductPrice: PaymentPlugin<any, any>['getProductPrice'],
) => {
  const productIDs = participations.map(p => p.productID as string);
  const products = await getProductsByIDs(productIDs)({
    _class: 1,
    _id: 1,
    name: 1,
    description: 1,
    translatedDescriptionHTML: { _class: 1, langKey: 1, text: 1 },
    sortingWeight: 1,
    hasPrice: 1,
    categoryID: 1,
    institutionID: 1,
    timeframeID: 1,
    attachedCertificate: { _class: 1, certificateID: 1, exportDate: 1 },
    publishDate: 1,
    topic: 1,
    language: 1,
    presenceType: 1,
    isCanceled: 1,
    appointments: { _class: 1, _id: 1, start: 1, end: 1, isNonBlocking: 1, location: 1, name: 1 },
    capacity: 1,

    hideCapacity: 1,
    prices: selectionSet(() => Price, 2),
  });

  const user = await getUserByID((participations[0]?.userID as string) ?? '')({
    _id: 1,
    firstName: 1,
    lastName: 1,
    title: 1,
    email: 1,
  });
  if (!user) throw new Error('could not find user');

  // validate data
  for (const p of products) {
    if (p.institutionID !== institutionID) {
      logger.error({ id: p.institutionID, institutionID }, 'not supported');
      throw new Error('buing from multiple institution at the same time is not supported');
    }
  }

  let fullAmount = 0;
  const choosenProducts = await Promise.all(
    products.map(
      async product =>
        new ChosenProduct({
          _id: A.getGloballyUniqueID(),
          amount: 1,
          product,
          priceID: await (async () => {
            const price = await getProductPrice(product._id, user._id);
            if (!price) return null;
            fullAmount += price.unitAmount;
            return price._id;
          })(),
        }),
    ),
  );

  return new BHPayment({
    status: 'created',
    participationIDs: participations.map(pa => pa._id),
    products: choosenProducts,
    amount: fullAmount,
  } as any);
};

export const invoiceForProductShouldBeSend = async (products: Product[]) => {
  let sendInvoiceMailNow = false;
  for (const product of products) {
    if (!(product instanceof Workshop)) continue;
    const start = product.appointments[0]?.start;
    const s = await getWorkshopByID(product._id)({
      settings: { sendInvoiceDaysInAdvance: 1, inheritedSendInvoiceDaysInAdvance: 1 },
    }).then(w => w?.settings);
    if (!s) throw new Error('Settings should be defined');
    // FIXME: settings should not return a string
    const inheritedDaysStr = s?.inheritedSendInvoiceDaysInAdvance?.[1] as Maybe<string>;
    const sendDaysInAdvance = s?.sendInvoiceDaysInAdvance ?? (inheritedDaysStr ? parseInt(inheritedDaysStr) : null);

    sendInvoiceMailNow ||=
      !start || // we'll send invoice to products without start date at the time of booking
      (() => {
        if (Number.isNaN(sendDaysInAdvance) || typeof sendDaysInAdvance !== 'number') return true; // no value specified => send mail now
        const deadline = subDays(start, sendDaysInAdvance);
        return isBefore(deadline, new Date()); // send mail when deadline is already over
      })();
  }

  return sendInvoiceMailNow;
};
